import React, { useState, useEffect } from "react"; // Add useEffect here
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import GradientBackground from "./components/GradientCycle";
import ProjectsGrid from "./components/ProjectsGrid";
import Navigation from "./components/Navigation"; // Import Navigation
import About from "./pages/About"; // About Page
import Contact from "./pages/Contact"; // Contact Page
import Footer from './components/Footer'; // Footer
import Loading from "./components/Loading"; // New Loading component
import './font.css';
import styled from 'styled-components';

// Styled Components for Layout
const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh; /* Full viewport height */
`;

const ContentWrapper = styled.main`
  flex: 1; /* Allow this area to grow and push the footer down */
  display: flex;
  flex-direction: column;
`;

// Data for the carousels
const workData = [
  {
    image: "/images/ellewihtwo.gif", // Replace with actual image URL
    title: "ELLE Women In Hollywood",
    description: "Design system implementation, QA.",
    categories: ["Editorial Storytelling"],
    year: "2024",
    client: "ELLE Magazine",
    skills: "Front-end Engineering",
    role: "Design Systems Engineer",
    tools: [" HTML/CSS/JS"],
    liveLink: "https://www.elle.com/women-in-hollywood-2024/",
  },
  {
    image: "/images/t&c.png", // Replace with actual image URL
    title: "Town and Country Philanthropy Summit",
    description: "Design system implementation, QA.",
    categories: ["Editorial Storytelling"],
    year: "2024",
    client: "Town & Country",
    skills: "Front-end Engineering",
    role: "Design Systems Engineer",
    tools: [" HTML/CSS/JS"],
    liveLink: "https://www.townandcountrymag.com/philanthropy-issue-2024/",
  },
  {
    image: "/images/esquirebbb.png", // Replace with actual image URL
    title: "Esquire Big Black Book",
    description: "Owned interaction design through rapid prototyping of animations and collaborating with the front-end engineer to integrate animations into the existing design system.",
    categories: ["Editorial Storytelling"],
    year: "2024",
    client: "Esquire",
    skills: "Prototyping",
    role: "Creative Technologist",
    tools: [" HTML/CSS/JS, ", " GSAP"],
    liveLink: "https://www.esquire.com/style/big-black-book-summer-2024/",
  },
  {
    image: "/images/abuelastwo.gif", // Replace with actual image URL
    title: "Oprah Daily: From Our Abuelas",
    description: "With my own abuela in min with the concept of post card/love letter, I drove interaction design and creative direction through concept ideation and rapid prototyping, collaborating closely with the designer and art director to define the final user experience. Post-launch, we got feedback that one of the featured abuelas was deeply touched by the package design which served as a reminder to never undervalue simplicity and intentionality in human-centered design.",
    categories: ["Editorial Storytelling"],
    year: "2023",
    client: "Oprah Daily",
    skills: "User experience design, product strategy, creative direction, prototyper",
    role: "Creative Technologist",
    tools: [" HTML/CSS/JS,"," Codepen"],
    liveLink: "https://www.oprahdaily.com/interactive/a41281402/from-our-abuelas/",
  },
  {
    image: "/images/futures.png", // Replace with actual image URL
    title: "Oprah Daily: Black Futures Rising",
    description: "Led interaction and user experience design in collaboration with a product designer, utilizing prototypes to explore functionality and iterative testing to refine the strategy for a new experimental storytelling format. This approach boosted average scroll depth and doubled time spent on the page before developer handoff.",
    categories: ["Editorial Storytelling"],
    year: "2022",
    client: "Oprah Daily",
    skills: "User experience design, product strategy, prototyper",
    role: "Creative Technologist",
    tools: [" HTML/CSS/JS,"," Codepen"],
    liveLink: "https://www.oprahdaily.com/interactive/a40229418/futurerising/",
  },
  {
    image: "/images/fffinal.gif", // Replace with actual image URL
    title: "Elle Fashion Finder",
    description: "How can ELLE's authority in style foster deeper, habitual engagement with its audience? We began with a simple question: \"How do people get dressed?\" From there, we explored how individuals across the country—united by a desire to feel confident in their clothing—could be empowered daily by the brand's expertise. Collaborating with the User Research Director, I led ideation, prototyping, and user testing to validate a styling assistant community tool. This project also uncovered untapped opportunities in the virtual styling space, providing foundational research for future innovation.",
    categories: ["Research & Development"],
    year: "2024",
    client: "Elle Magazine",
    skills: "User research and interview moderation, insight analysis, prototyping, prompt design",
    role: "User Researcher and Prototyper",
    tools: ["Usertesting,", " HTML/CSS/JS,"," Figma,"," Codepen"],
  },
  {
    image: "/images/horoscopes.gif", // Replace with actual image URL
    title: "ELLE Horoscopes",
    description: "How can ELLE deepen engagement with its loyal horoscopes community while driving outcomes like increased loyalty, return visits, and time spent on the site? Collaborating with a product manager, I led user research and rapid prototyping to explore opportunities at the intersection of astrology and AI. We began by uncovering insights into the role astrology plays in individuals' lives. This initiative marked the start of an ongoing product discovery practice at Hearst Magazines, demonstrating a scalable framework for cross functional and deeply collaborative innovation.",
    categories: ["Research & Development"],
    year: "2023",
    client: "Elle Magazine",
    skills: "User research and interview moderation, insight analysis, prototyping, prompt design",
    role: "User Researcher and Prototyper",
    tools: ["Userlytics,", " HTML/CSS/JS,"," Figma,"," Codepen"],
  },
  {
    image: "/images/mars.gif", // Replace with actual image URL
    title: "Mars Inc Homepage Redesign",
    description: "Prototype Mars homepage to validate user a seamless user experience rich with scroll animations.",
    categories: ["Prototyping"],
    year: "2017",
    client: "Mars Inc.",
    skills: "Prototyping, animation",
    role: "Creative Technologist",
    tools: ["Webflow,", " HTML/CSS/JS"],
    liveLink: "https://www.mars.com/",
  },
  {
    image: "/images/resizedalexa2.gif", // Replace with actual image URL
    title: "Astra Zeneca Voicebot",
    description: "In collaboration with our user experience designer, I prototyped a pilot voice assistant to be deployed in outpatient oncology care. Integrated Alexa SDK with Dialogflow.",
    categories: ["Prototyping"],
    year: "2018",
    client: "Astra Zeneca",
    skills: "Bot development, conversation design",
    role: "User Experience Design, Implementation",
    tools: ["AWS,", " Dialogflow"],
  },
  {
    image: "/images/newnew.gif", // Replace with actual image URL
    title: "SoftSpot",
    description: "Grad school thesis. A speculative design and prototype of companion furniture installed at Weber Shandwick Offices. SoftSpot, embedded with a microcontroller and integrated with a custom web app leveraging wellness metrics via HealthKit for iOS, offers a conversational experience. It bridges how we engage with our wellness and something we often rely on—something soft, like a couch. Click the link to explore an overview.",
    categories: ["Prototyping"],
    year: "2019",
    client: "",
    skills: "Bot development, conversation design, user experience design, IoT integration, physical computing",
    role: "User Experience Design, Implementation",
    tools: ["AWS,", " Dialogflow,"," Arduino,"," Swift,", " OpenCB"],
    buttonAction: "read",
    liveLink: "https://docs.google.com/presentation/d/1VnUmZg5ahO7Uu8HR3-6_kkCVpU_16j7DVaV6DGkjLd0/edit?usp=sharing",
  },
  {
    image: "/images/argall2.jpeg", // Replace with actual image URL
    title: "Weber Shandwick x Ron Brown Leaders Network Augmented Reality Gallery",
    description: "At the 2018 Ron Brown Leaders Network Summit, Weber Shandwick sought to inspire youth by showcasing their diverse leadership—even when some leaders couldn’t attend in person. The solution? An AR Gallery that introduced aspiring PR professionals to agency leadership, helping them envision prospective roles and futures.",
    categories: ["Prototyping"],
    year: "2018",
    client: "Weber Shandwick",
    skills: "Web app development",
    role: "Developer",
    tools: ["Three.js,", " HTML/CSS", " WebGL,"," AR.js"],
  },
  {
    image: "/images/futuresarticle.png", // Replace with actual image URL
    title: "Algorithmic Bias and You",
    description: "Think piece I wrote on the role of humans in circumventing AI bias.",
    categories: ["Community & Thought"],
    year: "2021",
    buttonAction: "read",
    liveLink: "https://futures.webershandwick.com/newsletters/algorithmic-bias-and-you/",
  },
  {
    image: "/images/cc.png", // Replace with actual image URL
    title: "What Comes After The Coherence Crash",
    description: "In 2020, we researched how people sought meaning and agency during a period of widespread uncertainty. Amid a coherence crash—defined by the decline of institutions like religion, marriage, higher education, and media trust—this uncertainty was heightened by climate crises, rapid technological change, and a pivotal election.",
    categories: ["Community & Thought"],
    year: "2020",
    buttonAction: "read",
    liveLink: "https://www.scribd.com/document/486834994/What-Comes-After-the-Coherence-Crash",
  },
  /*{
    image:  "/images/douglaschat.jpg", // Replace with actual image URL
    title: "Survival of the Richest with Douglas Rushkoff",
    description:"Douglas and I have an indept conversation about his latest book and how organizations can support human centered design.",
    categories: ["Community & Thought"],
    tags: ["Podcast", "2024"],
    buttonAction: "listen",
    year: "2022",
    liveLink: "https://futures.webershandwick.com/newsletters/algorithmic-bias-and-you/",
  },*/
];

function App() {
  const [activeColor, setActiveColor] = useState("#FF4500");
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    // Simulate a loading delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000); // Adjust this delay as needed (e.g., 2000ms = 2 seconds)

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log("Active dynamic color:", activeColor);
  }, [activeColor]);

  // If loading, show the loading component
  if (loading) {
    return <Loading />;
  }

  return (
    <Router>
      <AppContainer>
        <Navigation />
        <ContentWrapper>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <GradientBackground onUpdateActiveColor={setActiveColor} />
                  <ProjectsGrid projects={workData} dynamicColor={activeColor} />
                </>
              }
            />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </ContentWrapper>
        <Footer />
      </AppContainer>
    </Router>
  );
}

export default App;